import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, Button,
  FormControlLabel, Checkbox,
} from '@mui/material';

// Reusable MUI replacements for window.prompt / window.confirm.

export interface LinkValue { href: string; newTab: boolean }

/** Link editor: URL + an "open in new tab" toggle the author controls per link. */
export function LinkDialog({
  open, initial = { href: 'https://', newTab: false }, onClose,
}: {
  open: boolean; initial?: LinkValue; onClose: (value: LinkValue | null) => void;
}) {
  const [href, setHref] = useState(initial.href);
  const [newTab, setNewTab] = useState(initial.newTab);
  useEffect(() => { if (open) { setHref(initial.href); setNewTab(initial.newTab); } }, [open, initial.href, initial.newTab]);
  const submit = () => { const v = href.trim(); if (v) onClose({ href: v, newTab }); };
  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Add link</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth margin="dense" label="URL" value={href}
          onChange={(e) => setHref(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        />
        <FormControlLabel
          control={<Checkbox checked={newTab} onChange={(e) => setNewTab(e.target.checked)} />}
          label="Open in new tab"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!href.trim()}>Add link</Button>
      </DialogActions>
    </Dialog>
  );
}

export function PromptDialog({
  open, title, label, initial = '', confirmLabel = 'OK', placeholder, onClose,
}: {
  open: boolean; title: string; label?: string; initial?: string; confirmLabel?: string;
  placeholder?: string; onClose: (value: string | null) => void;
}) {
  const [value, setValue] = useState(initial);
  useEffect(() => { if (open) setValue(initial); }, [open, initial]);
  const submit = () => { const v = value.trim(); if (v) onClose(v); };
  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth margin="dense" label={label} placeholder={placeholder} value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!value.trim()}>{confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function ConfirmDialog({
  open, title, message, confirmLabel = 'Confirm', danger = false, onClose,
}: {
  open: boolean; title: string; message: string; confirmLabel?: string; danger?: boolean;
  onClose: (ok: boolean) => void;
}) {
  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent><DialogContentText>{message}</DialogContentText></DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button variant="contained" color={danger ? 'error' : 'primary'} onClick={() => onClose(true)}>{confirmLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}
